import React, { useState, useEffect, useRef } from 'react'

import _ from 'lodash'
import cx from 'classnames'
import { InputGroup, Form, Button } from 'react-bootstrap'
import { SeekContext } from '../../core/context'

import { getColorsConfig } from '../../../../components/AssetViewer/helper/cssHelper'
import { injectAktivFallback } from '../../../widgets/widget.helper'

import styles from './searchBox.module.scss'

const SearchBoxComponent = ({ className, widgetProps, value, onChange, onSubmit, breakpoint, newChat=false, disabled=true, handleSuggestionClick, getChatHistory }) => {
  const seekContext = React.useContext(SeekContext);
  const [searchBoxStyles, setSearchBoxStyles] = useState({
    fontStyles: {},
    inputFieldStyle: {},
    placeHolderStyle: {},
    promptStyle: {}
  })

  const id = _.get(widgetProps, 'uid')
  const widgetProperties = _.get(widgetProps, 'properties')
  const seekBarProps = _.get(
    widgetProperties,
    `${breakpoint}.questionDesign.bar`,
    {}
  )
  const seekResponseProps = _.get(
    widgetProperties,
    `${breakpoint}.responseDesign`,
    {}
  )

  const responseStyle = _.get(seekResponseProps, `type`, 'inline');
  const accentColor = _.get(seekResponseProps, 'accentColor', {r: 245, g: 245, b: 245, a: 1});
  const session =  _.get(seekContext, `seekSession.${id}.item`);
  const loading =  _.get(seekContext, `seekSession.${id}.loading`, false);
  const querying = (_.get(session, 'chats') || []).find(chat => chat.querying) != null;

  const placeHolder = _.get(
    seekBarProps,
    'placeholderQuestion',
    'Enter your question here'
  )
  const promptEnabled = _.get(seekBarProps, 'promptEnabled', false)
  const promptQuestions = _.get(seekBarProps, 'prompts', [])

  useEffect(() => {
    updateSearchBoxStyles()
  }, [ widgetProperties, breakpoint])

  const updateSearchBoxStyles = () => {
    const inputFieldStyle = getSearchBarStyles(widgetProperties, breakpoint)
    const fontStyles = getFontStyles(widgetProperties, breakpoint)
    const placeHolderStyles = getPlaceholderStyles(widgetProperties, breakpoint)
    const promptStyles = getPromptStyles(widgetProperties, breakpoint);

    setSearchBoxStyles({
      inputFieldStyle: inputFieldStyle,
      fontStyles: fontStyles,
      placeHolderStyle: placeHolderStyles,
      promptStyle: promptStyles
    })
  }

  const wrapperStyles = getStyles(widgetProps, breakpoint, 'bar')

  const handleSubmit =()=>{
    onSubmit(value);
  }

  const handleStartNewChat = () => {
    seekContext.invalidateSeekSession(id);
  }

  return (
    <div
      id={`search_input_${id}`}
      style={wrapperStyles}
      className={cx(styles.searchBoxContainer, className)}
    >
      <div className={cx(styles.inputFieldWrapper)}>
        <InputGroup
          style={searchBoxStyles.inputFieldStyle}
          className={styles.InputGroup}
        >
          {
            <style>
              {`
                #${`search_input_${id}`} input[data-device=${breakpoint}]::-webkit-input-placeholder {
                  color: ${ _.get(searchBoxStyles, 'placeHolderStyle.color')};
                }
                #seekSearchButton i[data-device=${breakpoint}]::before {
                  color: ${_.get(searchBoxStyles, 'fontStyles.color')};
                }
            `}
            </style>
          }
          <input
            data-device={breakpoint}
            className={styles.searchField}
            style={searchBoxStyles.fontStyles}
            value={value}
            placeholder={placeHolder}
            disabled={querying || disabled}
            onChange={(e) => onChange(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.keyCode === 13) {
                e.preventDefault()
                handleSubmit()
              }
            }}
            id='seekSearch'
          />
          <Button
            className={styles.seekButton}
            variant='info'
            id='seekSearchButton'
            onClick={handleSubmit}
          >
            <i data-device={breakpoint} className={cx(styles.Icon, 'cleverstory-paperflite-logo')} />
          </Button>
        </InputGroup>
        <span className={styles.label}>Powered by OpenAI</span>
      </div>
      {(promptEnabled && (_.size(_.get(session, 'chats')) === 0) && !loading) && (
        <PromptQuestion
          promptQuestions={promptQuestions}
          newChat={newChat}
          disabled={querying || disabled}
          breakpoint={breakpoint}
          responseStyle={responseStyle}
          promptStyles={searchBoxStyles.promptStyle}
          onSelect={handleSuggestionClick}
        />
      )}
      { newChat && 
        <div id='newChatButton' className={styles.newChatWrapper}>
          <Button variant='secondary' disabled={querying || disabled} style={getColorStyles(accentColor)} className={styles.newChatButton} onClick={handleStartNewChat}>
            <i style={getColorStyles(accentColor)} className={cx(styles.Icon, 'cleverstory-plus')} />
            New Chat
          </Button>
          {
            <style>
              {`
                #newChatButton i::before {
                  color: ${ getColorsConfig(accentColor)};
                }
              `}
            </style>
          }
        </div>
      }
    </div>
  )
}

const PromptQuestion = ({
  onSelect,
  newChat,
  disabled,
  breakpoint,
  responseStyle,
  promptStyles,
  promptQuestions
}) => {
  const handleSelect = (suggestion) => {
    if(disabled) {
      return;
    }
    onSelect(suggestion)
  }
  return (
    <div
      className={cx(styles.promptsContainer, 
      {[styles.disabled]: disabled},
      {
        [styles.mobileViewPrompts]: (breakpoint === 'mobile' || (newChat && responseStyle === 'slider'))
      })}
    >
      {_.map(promptQuestions, (prompt, index) => {
        if(_.size(prompt) == 0) {
          return;
        }
        return (
          <div
            key={index}
            style={promptStyles}
            onClick={() => handleSelect(prompt)}
            className={styles.prompt}
          >
            {prompt}
          </div>
        )
      })}
    </div>
  )
}

const searchBoxEquals = function (prevProps, nextProps) {
  const paths = ['className', 'widgetProps', 'breakpoint', 'value', 'onChange', 'onSubmit', 'handleSuggestionClick', 'newChat', 'disabled']
  const next = _.pick(nextProps, paths)
  const previous = _.pick(prevProps, paths)

  const equals = _.isEqual(next, previous)
  return equals
}

export const SeekSearchBox = React.memo(SearchBoxComponent, searchBoxEquals)



function getStyles(widgetProps, breakpoint, type) {

  const deviceProperties = _.get(widgetProps, `properties.${breakpoint}`)
  const padding = _.get(deviceProperties, `padding`, 0)
  var widgetStyles = {}

  //background
  const backgroundRgb = _.get(
    deviceProperties,
    `responseDesign.backgroundColor`,
    null
  )

  var background = 'inherit'
  if (backgroundRgb) {
    background = getColorsConfig(backgroundRgb)
  }

  widgetStyles['background'] = background

  if (breakpoint === 'desktop') {
    widgetStyles = {
      ...widgetStyles,
      padding: `${padding}px`,
    }
  }

  if (breakpoint === 'mobile') {
    widgetStyles = {
      ...widgetStyles,
      padding: `${padding}px`,
    }
  }
  return widgetStyles
}

/**
 * --------------------------------------------------
 * Placeholder Styles
 * --------------------------------------------------
 */
export function getPlaceholderStyles(widgetProperties, breakpoint) {
  var styles = {}

  if (!widgetProperties) {
    return styles
  }

  const deviceProperties = _.get(widgetProperties, `${breakpoint}`)
  const fontStyles = _.get(deviceProperties, `font`, {})

  //font-color
  const colorRgb = _.get(fontStyles, `tertiaryColor`, null)
  const color = getColorStyles(colorRgb)
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = _.get(fontStyles, 'fontFamily')
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: fontFamily
    }
  }

  //font-size
  const fontSizeString = _.get(fontStyles, 'fontSize', null)
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px'
  }

  styles['fontSize'] = fontSize

  return styles
}

/**
 * 
 * Prompts styles
 */

function getPromptStyles(widgetProperties, breakpoint) {
  var styles = {}

  if (!widgetProperties) {
    return styles
  }

  const deviceProperties = _.get(widgetProperties, `${breakpoint}`)
  const fontStyles = _.get(deviceProperties, `font`, {})

  //font-color
  const colorRgb = _.get(fontStyles, `secondaryColor`, { r: 111, g: 111, b: 111, a: 1 })
  const color = getColorStyles(colorRgb)
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = _.get(fontStyles, 'fontFamily')
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: injectAktivFallback(fontFamily)
    }
  }

  return styles
}

/**
 * Generate SearchBar Styles
 *
 */
function getSearchBarStyles(widgetProperties, breakpoint) {
  var styles = {}

  if (!widgetProperties) {
    return styles
  }

  const defaultProperties = _.get(widgetProperties, `${breakpoint}`)
  const barStyles = _.get(defaultProperties, `questionDesign.bar`, {})

  //background
  const inputFieldColor = _.get(barStyles, `inputFieldColor`, {
    r: 255,
    g: 255,
    b: 255,
    a: 1
  })
  var background = 'inherit'
  if (inputFieldColor) {
    background = getColorsConfig(inputFieldColor)
  }

  styles['background'] = background

  //border
  const border = _.get(barStyles, `border`, {})

  const borderStyle = getBorderStyles(border)
  styles = {
    ...styles,
    ...borderStyle
  }

  return styles
}

export function getColorStyles(value) {
  if (!value) {
    return null
  }

  const colorRgb = value

  var color = 'inherit'
  if (colorRgb) {
    color = getColorsConfig(colorRgb)
  }

  var styles = {
    color: color
  }

  return styles
}

/**
 * Generate Font Styles
 *
 */
function getFontStyles(widgetProperties, breakpoint) {
  var styles = {}

  if (!widgetProperties) {
    return styles
  }

  const deviceProperties = _.get(widgetProperties, `${breakpoint}`)
  const fontStyles = _.get(deviceProperties, `font`, {})

  //font-color
  const colorRgb = _.get(fontStyles, `color`, null)
  const color = getColorStyles(colorRgb)
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = _.get(fontStyles, 'fontFamily')
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: injectAktivFallback(fontFamily)
    }
  }

  //font-size
  const fontSizeString = _.get(fontStyles, 'fontSize', "12")
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px'
  }

  styles['fontSize'] = fontSize

  return styles
}

export function getBorderStyles(border) {
  var styles = {}
  if (!border) {
    return styles
  }

  const topLeft = _.get(border, 'radius.topLeft', null)
  const topRight = _.get(border, 'radius.topRight', null)
  const bottomLeft = _.get(border, 'radius.bottomLeft', null)
  const bottomRight = _.get(border, 'radius.bottomRight', null)

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px'
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px'
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px'
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px'
  }

  const borderStyle = _.get(border, 'style', 'bottom');
  if (borderStyle !== 'none') {
    const borderWidthString = _.get(border, 'width') || 0
    var borderWidget = parseInt(borderWidthString) + 'px'

    const fieldName = borderStyle == 'all' ? 'borderWidth' : 'borderBottomWidth'
    styles[fieldName] = borderWidget

    const colorRgb = _.get(border, 'color')
    var color = 'transparent'
    if (colorRgb) {
      color = getColorsConfig(colorRgb)
    }

    styles['borderColor'] = color
  }

  return styles
}
